import { FunctionComponent, SVGAttributes, useState } from 'react';

import InfoIcon from '@/app/assets/svg/info.svg?react';
import EthIcon from '@/app/assets/token/eth.svg?react';
import TetrationIcon from '@/app/assets/token/tetration.svg?react';
import UsdcIcon from '@/app/assets/token/UsdcIcon';
import WyldsIcon from '@/app/assets/token/wylds.png';
import {
  COMING_SOON_VAULTS,
  useVaults,
  VaultTableRow,
} from '@/app/components/VaultsPageComponents/hooks/useVaults';
import VaultAvatar from '@/app/components/VaultsPageComponents/VaultAvatar';
import VaultInfoModal from '@/app/components/VaultsPageComponents/VaultInfoModal';
import { getMarketDetailsPath } from '@/app/providers/router/routes';
import { RateBasis } from '@/app/shared/constants/vaults';
import { cn } from '@/app/shared/lib/cn';
import { formatApyLabel } from '@/app/shared/lib/vaultFormatters';
import { AppLink } from '@/app/shared/ui/AppLink';
import { Button } from '@/app/shared/ui/Button';
import DataTable from '@/app/shared/ui/DataTable.tsx';

const desktopGridClassName =
  'min-[950px]:grid min-[950px]:grid-cols-[minmax(180px,1.5fr)_repeat(3,minmax(56px,0.5fr))_minmax(24px,0.55fr)_minmax(58px,0.8fr)_repeat(3,minmax(72px,1fr))] min-[950px]:items-center min-[950px]:gap-4 min-[950px]:px-5';

type MetaIcon = FunctionComponent<SVGAttributes<SVGElement>>;

interface VaultsTableProps {
  activeBasis: RateBasis;
}

const VaultStat = ({
  label,
  value,
  sub,
  valueClassName,
  blurred,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClassName?: string;
  blurred?: boolean;
}) => (
  <div
    className={cn(
      'flex min-w-0 flex-col gap-2 min-[600px]:gap-1.5 min-[950px]:gap-0.5',
      blurred && 'select-none blur-[6px]',
    )}
  >
    <span className='text-sm font-medium tracking-[0.075em] text-gray-500 min-[950px]:hidden'>{label}</span>
    <div className='flex min-w-0 flex-col gap-0.5 font-normal'>
      <span className={cn('truncate text-base text-gray-200', valueClassName)}>{value}</span>
      {sub != null && <span className='truncate text-xs text-gray-500'>{sub}</span>}
    </div>
  </div>
);

const VaultMeta = ({
  label,
  value,
  Icon,
  iconSrc,
  blurred,
}: {
  label: string;
  value: string;
  Icon?: MetaIcon;
  iconSrc?: string;
  blurred?: boolean;
}) => (
  <div
    className={cn(
      'flex min-w-0 flex-col gap-2 min-[600px]:gap-1.5 min-[950px]:items-center min-[950px]:gap-1',
      blurred && 'select-none blur-[6px]',
    )}
  >
    <span className='text-sm font-medium tracking-[0.075em] text-gray-500 min-[950px]:hidden'>{label}</span>
    <div className='flex flex-col gap-1 min-[950px]:items-center'>
      {!blurred && Icon != null ? (
        <Icon className='h-8 w-8 shrink-0 min-[950px]:h-6 min-[950px]:w-6' aria-hidden />
      ) : !blurred && iconSrc != null ? (
        <img
          src={iconSrc}
          alt=''
          className='h-8 w-8 shrink-0 rounded-full object-cover min-[950px]:h-6 min-[950px]:w-6'
          aria-hidden
        />
      ) : (
        <span className='h-8 w-8 shrink-0 rounded-full bg-gray-900 min-[950px]:h-6 min-[950px]:w-6' aria-hidden />
      )}
      <span className={cn('truncate text-xs text-gray-500', blurred && 'hidden min-[950px]:inline')}>
        {blurred ? label : value}
      </span>
    </div>
  </div>
);

const assetMetaIcon = (asset: string): { Icon?: MetaIcon; iconSrc?: string } => {
  if (asset.toUpperCase() === 'WYLDS') return { iconSrc: WyldsIcon };
  if (asset.toUpperCase() === 'USDC') return { Icon: UsdcIcon };
  return {};
};

const VaultRowContent = ({
  vault,
  blurred,
  onInfoClick,
}: {
  vault: VaultTableRow;
  blurred?: boolean;
  onInfoClick?: () => void;
}) => (
  <div className='grid grid-cols-2 items-start gap-x-4 gap-y-4 min-[600px]:grid-cols-4 min-[600px]:gap-x-4 min-[600px]:gap-y-5 min-[950px]:contents'>
    <div className='col-span-2 flex min-w-0 flex-col gap-0.5 min-[600px]:col-span-4 min-[950px]:col-span-1 min-[950px]:flex-row min-[950px]:items-center min-[950px]:gap-3'>
      <span
        className={cn(
          'text-xs font-medium tracking-[0.075em] text-gray-500 min-[950px]:hidden',
          blurred && 'select-none blur-[6px]',
        )}
      >
        Market
      </span>
      <div className='flex min-w-0 items-center gap-3'>
        <VaultAvatar vaultId={vault.id} name={vault.name} className={cn(blurred && 'blur-[6px]')} />
        <div className={cn('flex min-w-0 flex-col gap-0.5', blurred && 'select-none blur-[6px]')}>
          <div className='flex items-center gap-1.5'>
            <span className='truncate text-base text-gray-200'>{vault.name}</span>
            {onInfoClick != null ? (
              <Button
                type='button'
                variant='ghost'
                aria-label={`${vault.name} vault information`}
                className='h-auto shrink-0 rounded-none p-0 text-gray-600 hover:bg-transparent hover:text-gray-400'
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onInfoClick();
                }}
              >
                <InfoIcon className='h-3.5 w-3.5' />
              </Button>
            ) : (
              <InfoIcon className='h-3.5 w-3.5 shrink-0 text-gray-600' aria-hidden />
            )}
          </div>
          <span className='text-xs text-gray-500'>{`${vault.asset} · ${vault.chain}`}</span>
        </div>
      </div>
    </div>

    <VaultMeta label='Chain' value={vault.chain} Icon={EthIcon} blurred={blurred} />
    <VaultMeta label='Asset' value={vault.asset} {...assetMetaIcon(vault.asset)} blurred={blurred} />
    <VaultMeta label='Curator' value={vault.curator} Icon={TetrationIcon} blurred={blurred} />
    <div className='hidden min-[600px]:block min-[950px]:hidden' aria-hidden />
    <div className='hidden min-[950px]:block' aria-hidden />
    <VaultStat label='TVL' value={vault.tvl} sub={vault.tvlBase} blurred={blurred} />
    <VaultStat label='Base' value={formatApyLabel(vault.baseApy)} blurred={blurred} />
    <VaultStat
      label='Senior'
      value={formatApyLabel(vault.seniorApy)}
      sub={vault.seniorShare}
      valueClassName='text-brand-1100'
      blurred={blurred}
    />
    <VaultStat
      label='Junior'
      value={formatApyLabel(vault.juniorApy)}
      sub={vault.juniorShare}
        valueClassName='text-apy-value'
      blurred={blurred}
    />
  </div>
);

const VaultsTable = ({ activeBasis }: VaultsTableProps) => {
  const [infoVault, setInfoVault] = useState<{ id: string; name: string } | null>(null);
  const { vaults } = useVaults(activeBasis);
  const rows = [...vaults, ...COMING_SOON_VAULTS];

  return (
    <div className='flex flex-col min-[950px]:gap-3'>
      <DataTable
        data={rows}
        getRowKey={(row) => row.id}
        desktopGridClassName={desktopGridClassName}
        desktopMinWidth={950}
        pagination={{ desktop: 5, mobile: 2, initialPage: 1, mobileMaxWidth: 950 }}
        headerCellClassName='text-sm font-medium tracking-[0.075em] text-gray-500'
        className='min-[950px]:gap-3'
        headers={VAULTS_HEADERS}
        renderRow={(vault) => {
          if (vault.isComingSoon) {
            return (
              <div
                aria-hidden
                className={cn(
                  desktopGridClassName,
                  'pointer-events-none flex cursor-default flex-col gap-4 rounded-md bg-elevated-card-2 p-4 font-normal min-[950px]:h-18.5 min-[950px]:flex-none min-[950px]:gap-4 min-[950px]:py-4',
                )}
              >
                <VaultRowContent vault={vault} blurred />
              </div>
            );
          }

          return (
            <AppLink
              to={getMarketDetailsPath(vault.id)}
              className={cn(
                desktopGridClassName,
                'flex cursor-pointer flex-col gap-4 rounded-md bg-elevated-card-2 p-4 font-normal hover:bg-elevated-card-hover min-[950px]:h-18.5 min-[950px]:flex-none min-[950px]:gap-4 min-[950px]:py-4',
              )}
            >
              <VaultRowContent
                vault={vault}
                onInfoClick={() => setInfoVault({ id: vault.id, name: vault.name })}
              />
            </AppLink>
          );
        }}
      />

      <VaultInfoModal
        isOpen={infoVault != null}
        vaultId={infoVault?.id ?? null}
        vaultName={infoVault?.name}
        onClose={() => setInfoVault(null)}
      />
    </div>
  );
};

export default VaultsTable;

const metaHeader = (label: string) => (
  <span className='block w-full text-center'>{label}</span>
);

const VAULTS_HEADERS = [
  'Market',
  metaHeader('Chain'),
  metaHeader('Asset'),
  metaHeader('Curator'),
  '',
  'TVL',
  'Base',
  'Senior',
  'Junior',
];
